import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, timeAgo } from "../lib/api";

// People analytics: pick one or all employers / jobseekers, search and page through
// them, and see real per-account metrics. Emails stay masked; revealing one is audited.
export function PeopleAnalytics() {
  const [type, setType] = useState("employers");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [revealed, setRevealed] = useState({});

  const load = (t = type, p = page, query = q) => {
    api.get("/admin/analytics/people", { params: { type: t, page: p, limit: 25, q: query } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e, "Couldn't load analytics")));
  };
  useEffect(() => { load(); }, [type, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (id) => {
    setDetail(null);
    api.get(`/admin/analytics/people/${id}`).then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiError(e, "Couldn't load that account")));
  };

  const reveal = (id) => {
    api.post("/admin/analytics/reveal-email", { user_id: id })
      .then((r) => { setRevealed((prev) => ({ ...prev, [id]: r.data.email })); toast.success("Email revealed — this was recorded in the audit log"); })
      .catch((e) => toast.error(formatApiError(e, "Couldn't reveal the email")));
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const stat = (label, value, testid) => (
    <div className="border border-zinc-200 rounded-md p-3" data-testid={testid}>
      <p className="text-lg font-bold text-zinc-950">{value}</p>
      <p className="text-[11px] text-zinc-500">{label}</p>
    </div>
  );

  return (
    <div data-testid="people-analytics">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-1" data-testid="people-type-toggle">
          {[["employers", "Employers"], ["candidates", "Jobseekers"]].map(([id, label]) => (
            <button key={id} onClick={() => { setType(id); setPage(1); setDetail(null); }} data-testid={`people-type-${id}`}
              className={`h-8 px-3 text-xs font-semibold rounded transition-colors duration-200 ${type === id ? "bg-zinc-950 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>
              {label}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setDetail(null); load(type, 1, q); }} className="flex gap-2 flex-1 min-w-52">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company or email…"
            data-testid="people-search"
            className="flex-1 h-9 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
          <button type="submit" data-testid="people-search-submit"
            className="h-9 px-4 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200">Search</button>
        </form>
        {data && <p className="text-xs text-zinc-500" data-testid="people-total">{data.total.toLocaleString()} {type === "employers" ? "employers" : "jobseekers"}</p>}
      </div>

      {!data ? (
        <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="people-loading" />
      ) : data.items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center text-sm text-zinc-500" data-testid="people-empty">No accounts match that search.</div>
      ) : (
        <div className="space-y-2" data-testid="people-list">
          {data.items.map((p) => (
            <button key={p.id} onClick={() => openDetail(p.id)} data-testid={`people-row-${p.id}`}
              className={`w-full text-left border rounded-md p-4 transition-colors duration-200 ${detail?.id === p.id ? "border-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-sm font-semibold text-zinc-950">{p.company || p.name}</span>
                <span className="text-xs text-zinc-500">{revealed[p.id] || p.email_masked}</span>
                {type === "employers" ? (
                  <span className="text-xs text-zinc-500">{p.live_jobs} live · {p.jobs} total · {p.job_views.toLocaleString()} views{p.team_size ? ` · team of ${p.team_size + 1}` : ""}</span>
                ) : (
                  <span className="text-xs text-zinc-500">{p.applications} applications{p.location ? ` · ${p.location}` : ""}{p.cv ? " · CV on file" : ""}</span>
                )}
                <span className="text-[11px] text-zinc-400 ml-auto">joined {timeAgo(p.joined)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3" data-testid="people-pagination">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="people-prev"
            className="px-4 py-2 border border-zinc-300 rounded-md text-sm font-medium disabled:opacity-40 hover:border-zinc-400 transition-colors duration-200">Previous</button>
          <span className="text-sm text-zinc-500">Page {page} of {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="people-next"
            className="px-4 py-2 border border-zinc-300 rounded-md text-sm font-medium disabled:opacity-40 hover:border-zinc-400 transition-colors duration-200">Next</button>
        </div>
      )}

      {detail && (
        <div className="mt-6 border border-zinc-200 rounded-md p-5 bg-zinc-50/50" data-testid="people-detail">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <p className="font-display text-lg font-bold text-zinc-950">{detail.company || detail.name}</p>
            <span className="text-xs text-zinc-500">{revealed[detail.id] || detail.email_masked}</span>
            <button onClick={() => reveal(detail.id)} data-testid="people-reveal-email"
              className="text-[11px] font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2">
              {revealed[detail.id] ? "Revealed (audited)" : "Reveal email (audited)"}
            </button>
            <span className="text-[11px] text-zinc-400 ml-auto">
              {detail.verified ? "verified" : "unverified"} · {detail.account_status}{detail.last_seen ? ` · last seen ${timeAgo(detail.last_seen)}` : ""}
            </span>
          </div>
          {detail.role === "employer" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {stat("Live jobs", detail.jobs_live, "pd-live-jobs")}
              {stat("Total jobs", detail.jobs_total, "pd-total-jobs")}
              {stat("Paused", detail.jobs_paused, "pd-paused-jobs")}
              {stat("Job views", (detail.job_views || 0).toLocaleString(), "pd-job-views")}
              {stat("Applications", detail.applications_total, "pd-apps")}
              {stat("Team members", (detail.team || []).length, "pd-team")}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {stat("Applications", detail.applications_total, "pd-apps")}
              {stat("Saved jobs", detail.saved_jobs, "pd-saved")}
              {stat("Saved searches", detail.saved_searches, "pd-searches")}
              {stat("CV on file", detail.cv ? "Yes" : "No", "pd-cv")}
              {stat("Talent pool", detail.discoverable ? "Opted in" : "Not opted in", "pd-discoverable")}
            </div>
          )}
          {detail.applications_by_status && Object.keys(detail.applications_by_status).length > 0 && (
            <p className="mt-3 text-xs text-zinc-500" data-testid="pd-app-status">
              Applications by status: {Object.entries(detail.applications_by_status).map(([k, v]) => `${k} ${v}`).join(" · ")}
            </p>
          )}
          {detail.role === "employer" && (detail.team || []).length > 0 && (
            <p className="mt-3 text-xs text-zinc-500" data-testid="pd-team-list">
              Team: {detail.team.map((m) => `${m.name} (${m.team_role})`).join(" · ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
